/**
 * Beta Access Page - Shown to users who don't have beta access
 * Available at /beta-access
 * Options: Login (for existing testers), Enter invite code, Join waitlist
 * 
 * FLOW FOR NEW USERS WITH INVITE CODE:
 * 1. Click "I have an invite code"
 * 2. Enter code first (no login required)
 * 3. Code is validated (but not redeemed yet)
 * 4. Validated code is stored in localStorage
 * 5. User is redirected to EXISTING signup/login flow in /app
 * 6. App.js handles code redemption after signup/login
 */
import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { Shield, Key, Mail, LogIn, ArrowLeft, Check, Loader2, UserPlus, Smartphone } from 'lucide-react';

const BetaAccessPage = () => {
  // Views: main, login, code, code_validated, waitlist
  const [view, setView] = useState('main');
  const [loading, setLoading] = useState(false);
  const [betaMetrics, setBetaMetrics] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Invite code form
  const [inviteCode, setInviteCode] = useState('');
  // Validated code state - preserved across signup/login
  const [validatedCode, setValidatedCode] = useState(null);

  // Waitlist form
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);

  // Check for referral params
  const urlParams = new URLSearchParams(window.location.search);
  const referredBy = urlParams.get('ref');
  const streamId = urlParams.get('stream');

  // Helper: redirect to app, or to stored stream if user came from shared link
  const redirectToApp = () => {
    try {
      const returnStream = JSON.parse(localStorage.getItem('gems_return_stream') || 'null');
      if (returnStream && returnStream.streamId && (Date.now() - returnStream.timestamp) < 3600000) {
        localStorage.removeItem('gems_return_stream');
        window.location.href = `/app/watch/${returnStream.streamId}`;
        return;
      }
    } catch {}
    window.location.href = '/app';
  };

  // Helper: redirect to existing signup flow with validated code
  const redirectToSignup = () => {
    // Store the validated code so the main app can use it
    localStorage.setItem('gems_pending_beta_code', validatedCode);
    // Store referral info if present
    if (referredBy) {
      localStorage.setItem('gems_referred_by', referredBy);
    }
    if (streamId) {
      localStorage.setItem('gems_signup_source', 'livestream_share');
    }
    // Redirect to /app with signup flag - bypasses beta gate for auth flow
    window.location.href = '/app?signup=true';
  };

  // Helper: redirect to login flow with validated code
  const redirectToLogin = () => {
    // Store the validated code so the main app can use it
    localStorage.setItem('gems_pending_beta_code', validatedCode);
    // Redirect to /app with login flag - bypasses beta gate for auth flow
    window.location.href = '/app?login=true';
  };

  useEffect(() => {
    fetchBetaMetrics();
    checkAuth();
  }, []);

  const fetchBetaMetrics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/beta/metrics`);
      if (res.ok) {
        setBetaMetrics(await res.json());
      }
    } catch (e) { /* ignore */ }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/beta/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated) {
          setIsAuthenticated(true);
          setCurrentUser(data);
          if (data.beta_access) {
            redirectToApp();
            return;
          }
          setView('code');
        }
      }
    } catch (e) { /* ignore */ }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/email-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('authToken', data.session_token);
        
        // If we have a validated code, redeem it now
        if (validatedCode) {
          await redeemValidatedCode(data.session_token);
          return;
        }
        
        // Check if user already has beta access
        if (data.user?.beta_access) {
          toast.success('Welcome back! Redirecting to app...');
          setTimeout(() => { redirectToApp(); }, 1000);
        } else {
          setIsAuthenticated(true);
          setCurrentUser(data.user);
          toast.info('Enter an invite code to finish setting up your account.');
          setView('code');
        }
      } else {
        toast.error(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Validate code WITHOUT authentication (for new users)
  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    setLoading(true);
    try {
      // First, just validate the code exists and is usable (no auth required)
      const res = await fetch(`${API_BASE_URL}/api/beta/check-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: inviteCode.trim() })
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        // Code is valid! Save it and show signup/login options
        setValidatedCode(inviteCode.trim().toUpperCase());
        toast.success('Valid code! Now create an account or log in to continue.');
        setView('code_validated');
      } else {
        toast.error(data.detail || 'Invalid or expired code');
      }
    } catch (error) {
      toast.error('Failed to validate code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Redeem a validated code after login
  const redeemValidatedCode = async (token) => {
    try {
      const body = {
        code: validatedCode,
        ...(referredBy && { referred_by_user_id: referredBy }),
        ...(streamId && { stream_id: streamId }),
        ...(streamId && { signup_source: 'livestream_share' })
      };
      const res = await fetch(`${API_BASE_URL}/api/beta/validate-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('You\'re in. Welcome to GEMS.');
        setTimeout(() => { redirectToApp(); }, 1500);
      } else {
        toast.error(data.detail || 'Failed to redeem code');
      }
    } catch (error) {
      toast.error('Failed to redeem code. Please try again.');
    }
  };

  // Redeem code for already-authenticated users
  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        // No auth - validate code first, then show options
        await handleValidateCode(e);
        return;
      }
      
      const body = {
        code: inviteCode.trim(),
        ...(referredBy && { referred_by_user_id: referredBy }),
        ...(streamId && { stream_id: streamId }),
        ...(streamId && { signup_source: 'livestream_share' })
      };
      const res = await fetch(`${API_BASE_URL}/api/beta/validate-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('You\'re in. Welcome to GEMS.');
        setTimeout(() => { redirectToApp(); }, 1500);
      } else {
        toast.error(data.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to validate code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: waitlistEmail.trim(),
          source: streamId ? 'livestream_viewer' : 'beta_access_page'
        })
      });
      if (res.ok) {
        setWaitlistSubmitted(true);
        toast.success('You\'re on the list!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Could not add you to the list');
      }
    } catch (error) {
      toast.error('Could not add you to the list. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-black flex flex-col items-center justify-center p-4" data-testid="beta-access-page">
      <Toaster position="top-right" />
      
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
            <Shield className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">GEMS</h1>
          <p className="text-gray-400">The Interactive Social Entertainment Platform</p>
        </div>

        {betaMetrics?.capacity_reached && (
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20">
              <span className="text-sm text-amber-400">
                New web sign-ups are paused for the moment. Leave your email and we'll send your invite.
              </span>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-gray-900/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 shadow-2xl">
          {/* Main Menu */}
          {view === 'main' && (
            <div className="space-y-3" data-testid="beta-main-menu">
              <a
                href="/get-app"
                className="w-full flex items-center gap-3 px-4 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors text-sm font-medium"
                data-testid="gate-get-app-primary"
              >
                <Smartphone className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-base font-semibold">Get the GEMS app</div>
                  <div className="text-emerald-100 text-xs">The best experience. Sign-up is open in the app.</div>
                </div>
              </a>

              <p className="text-gray-400 text-center text-xs pt-3 pb-1">
                Or continue on the web
              </p>

              <button
                onClick={() => setView('login')}
                className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-800 hover:bg-gray-750 text-white rounded-xl transition-colors text-sm font-medium border border-gray-700"
                data-testid="beta-login-btn"
              >
                <LogIn className="w-5 h-5 text-emerald-400" />
                <div className="text-left">
                  <div>Sign in</div>
                  <div className="text-gray-400 text-xs">Log in with your account</div>
                </div>
              </button>

              <button
                onClick={() => setView('code')}
                className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-800 hover:bg-gray-750 text-white rounded-xl transition-colors text-sm font-medium border border-gray-700"
                data-testid="beta-code-btn"
              >
                <Key className="w-5 h-5 text-cyan-400" />
                <div className="text-left">
                  <div>I have an invite code</div>
                  <div className="text-gray-400 text-xs">Create your account on the web</div>
                </div>
              </button>

              <button
                onClick={() => setView('waitlist')}
                className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-800 hover:bg-gray-750 text-white rounded-xl transition-colors text-sm font-medium border border-gray-700"
                data-testid="beta-waitlist-btn"
              >
                <Mail className="w-5 h-5 text-rose-400" />
                <div className="text-left">
                  <div>No code yet?</div>
                  <div className="text-gray-400 text-xs">Get an invite by email</div>
                </div>
              </button>

            </div>
          )}

          {/* Login Form - for returning beta users */}
          {view === 'login' && (
            <div data-testid="beta-login-form">
              <button onClick={() => setView('main')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-lg font-semibold text-white mb-1">Welcome back</h2>
              <p className="text-gray-400 text-sm mb-4">
                Enter your credentials to continue
              </p>
              <form onSubmit={handleLogin} className="space-y-3">
                <input
                  type="email"
                  placeholder="Email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  data-testid="beta-login-email"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  data-testid="beta-login-password"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="beta-login-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
              <p className="text-gray-500 text-xs text-center mt-3">
                Don't have an account? <button onClick={() => setView('code')} className="text-emerald-400 hover:underline">Enter an invite code</button>
              </p>
            </div>
          )}

          {/* Invite Code Entry Form - FIRST step for new users */}
          {view === 'code' && !isAuthenticated && (
            <div data-testid="beta-code-form">
              <button onClick={() => setView('main')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-lg font-semibold text-white mb-1">Enter Access Code</h2>
              <p className="text-gray-400 text-sm mb-4">
                Enter your invite code to get started
              </p>
              <form onSubmit={handleValidateCode} className="space-y-3">
                <input
                  type="text"
                  placeholder="Enter your code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm text-center font-mono tracking-wider focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  data-testid="beta-code-input"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="beta-code-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {loading ? 'Validating...' : 'Validate Code'}
                </button>
              </form>
              <p className="text-gray-500 text-xs text-center mt-3">
                Already have an account? <button onClick={() => setView('login')} className="text-emerald-400 hover:underline">Log in</button>
              </p>
            </div>
          )}

          {/* Code Validated - Route to EXISTING signup or login flow */}
          {view === 'code_validated' && (
            <div data-testid="beta-code-validated">
              <button onClick={() => { setValidatedCode(null); setView('code'); }} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 mb-3">
                  <Check className="w-6 h-6 text-emerald-400" />
                </div>
                <h2 className="text-lg font-semibold text-white mb-1">Code Validated!</h2>
                <p className="text-gray-400 text-sm">
                  Code <span className="font-mono text-emerald-400">{validatedCode}</span> is valid.
                </p>
              </div>
              
              <div className="space-y-3">
                {/* Route to EXISTING signup flow */}
                <button
                  onClick={redirectToSignup}
                  className="w-full flex items-center gap-3 px-4 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors text-sm font-medium"
                  data-testid="beta-signup-btn"
                >
                  <UserPlus className="w-5 h-5" />
                  <div className="text-left">
                    <div>Create Account</div>
                    <div className="text-emerald-200 text-xs">New to GEMS? Sign up now</div>
                  </div>
                </button>

                {/* Route to login with code redemption */}
                <button
                  onClick={() => setView('login_with_code')}
                  className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-800 hover:bg-gray-750 text-white rounded-xl transition-colors text-sm font-medium border border-gray-700"
                  data-testid="beta-login-with-code-btn"
                >
                  <LogIn className="w-5 h-5 text-cyan-400" />
                  <div className="text-left">
                    <div>Log In</div>
                    <div className="text-gray-400 text-xs">Already have an account? Sign in with this code</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Login with validated code - for existing users adding beta access */}
          {view === 'login_with_code' && (
            <div data-testid="beta-login-with-code-form">
              <button onClick={() => setView('code_validated')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-lg font-semibold text-white mb-1">Log In to Your Account</h2>
              <p className="text-gray-400 text-sm mb-4">
                Code <span className="font-mono text-emerald-400">{validatedCode}</span> will be redeemed after login.
              </p>
              <form onSubmit={handleLogin} className="space-y-3">
                <input
                  type="email"
                  placeholder="Email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  data-testid="beta-login-email-with-code"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  data-testid="beta-login-password-with-code"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="beta-login-with-code-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                  {loading ? 'Signing in...' : 'Log In & Redeem Code'}
                </button>
              </form>
            </div>
          )}

          {/* Invite Code Form - for authenticated users without beta access */}
          {view === 'code' && isAuthenticated && (
            <div data-testid="beta-code-form-authed">
              <button onClick={() => setView('main')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-lg font-semibold text-white mb-1">Enter Invite Code</h2>
              <p className="text-gray-400 text-sm mb-4">
                Logged in as <span className="text-emerald-400">{currentUser?.email || currentUser?.name}</span>. Enter your invite code to continue.
              </p>
              <form onSubmit={handleRedeemCode} className="space-y-3">
                <input
                  type="text"
                  placeholder="Enter your code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm text-center font-mono tracking-wider focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  data-testid="beta-code-input-authed"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="beta-code-submit-authed"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {loading ? 'Validating...' : 'Redeem Code'}
                </button>
              </form>
            </div>
          )}

          {/* Waitlist Form */}
          {view === 'waitlist' && (
            <div data-testid="beta-waitlist-form">
              <button onClick={() => setView('main')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {waitlistSubmitted ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 mb-4">
                    <Check className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white mb-2">You're on the list!</h2>
                  <p className="text-gray-400 text-sm">We'll email your invite.</p>
                </div>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-white mb-1">Get an invite</h2>
                  <p className="text-gray-400 text-sm mb-4">
                    Leave your email and we'll send you an invite code.
                  </p>
                  <form onSubmit={handleWaitlist} className="space-y-3">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                      data-testid="beta-waitlist-email"
                      required
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="beta-waitlist-submit"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      {loading ? 'Sending...' : 'Send my invite'}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-6">
          GEMS &middot; <a href="/" className="text-gray-500 hover:text-gray-400">Back to home</a>
        </p>
      </div>
    </div>
  );
};

export default BetaAccessPage;
